import { defaultAllSelection } from "services/constants/selections";
import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { Typography, Box, Stack, Paper, ButtonBase } from "@mui/material";
import {
  Timelapse,
  HolidayVillage,
  Savings,
  SettingsAccessibility,
  CropRotate,
  AccessTime,
  AccessTimeOutlined,
  CheckCircleOutline,
  HighlightOff,
  RemoveCircleOutline,
  KeyboardArrowRight,
  ArrowForward,
  AssignmentOutlined,
  BoltOutlined,
  PaymentsOutlined,
  ApprovalRounded,
  EditCalendarOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import {
  getThisMonthRange,
  resolvePresetLabel,
} from "components/CustomComponents/dateRangeDefaults";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { useSelector, useDispatch } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { customGetAPI } from "services/utils/CustomAPI";
import { subModuleNameMatches } from "services/utils/HelperFunction";
import { nodeHasAccess } from "services/utils/companyAccess";
import { WorkspaceUnavailableScreen } from "components/DesignSystem/WorkspaceUnavailableScreen";
import {
  designColors,
  designRadii,
  designTheme,
  designType,
  APP_BAR_HEIGHT,
} from "services/constants/designTokens";
import { StatCard } from "pages/TeamZone/Home/homeUi";
import { TeamAdvanceSalary } from "./AdvanceSalary";
import { Expense } from "./Expense";
import { OffDayDuty } from "./OffDayDuty";
import { FilterProvider } from "../../FilterContext";
import { setFilterApi } from "services/Redux/userManagament";
import { OverTime } from "./OverTime";
import { Leave } from "./Leave";
import { Permission } from "./Permission";
import { OnDuty } from "./OnDuty";
import { CompOff } from "./CompOff";
import { Break } from "./Break";
import { AttendanceCorrection } from "./AttendanceCorrection";
import { ShiftCorrection } from "./ShiftCorrection";
import { normalizeRangeDate, resolveRangeLabel } from "./dateRangeUtils";

const DEFAULT_ALL_SELECTION = defaultAllSelection;

const EMPTY_COUNT_SET = {
  pending: 0,
  approved: 0,
  rejected: 0,
  cancelled: 0,
};

const cloneCountSet = (counts = EMPTY_COUNT_SET) => ({
  pending: counts.pending ?? 0,
  approved: counts.approved ?? 0,
  rejected: counts.rejected ?? 0,
  cancelled: counts.cancelled ?? 0,
});

const statusKeyForCount = (status) => {
  const key = (status || "").trim().toLowerCase();
  if (key === "pending") return "pending";
  if (key === "approved") return "approved";
  if (key === "rejected") return "rejected";
  if (key === "cancelled" || key === "canceled") return "cancelled";
  return null;
};

const countRowsByStatus = (rows = []) =>
  rows.reduce((acc, row) => {
    const key = statusKeyForCount(row?.status);
    if (key) acc[key] += 1;
    return acc;
  }, cloneCountSet());

const rowsFromResponse = (res) => {
  const body = res?.responseDetails;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

const toOverviewMetric = (counts, metric) => cloneCountSet(counts)[metric] ?? 0;

const approvalMetricLabels = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
  cancelled: "cancelled",
};

const approvalCenterCards = [
  {
    title: "Leave",
    key: "Leaves",
    view: "leave",
    subtitle: "Leave Requests",
    pendingCount: 18,
    accent: "#6D5DF6",
    accentSoft: "#F4F1FF",
    icon: <HolidayVillage />,
  },
  {
    title: "Overtime",
    key: "Overtime",
    view: "overtime",
    subtitle: "Planned and completed OT approvals",
    pendingCount: 16,
    accent: "#F08A24",
    accentSoft: "#FFF4E8",
    icon: <Timelapse />,
  },
  {
    title: "Off-Day Duty",
    key: "Off Day Duty",
    view: "offday-duty",
    subtitle: "Week off and holiday duty approvals",
    pendingCount: 8,
    accent: "#F4A621",
    accentSoft: "#FFF7E6",
    icon: <SettingsAccessibility />,
  },
  {
    title: "Attendance Correction",
    key: "Attendance Correction",
    view: "attendance-correction",
    subtitle: "Punch correction approvals",
    pendingCount: 0,
    accent: "#12B76A",
    accentSoft: "#EAFBF4",
    icon: <EditCalendarOutlined />,
  },
  {
    title: "Schedule Change",
    key: "Schedule Change",
    view: "shift-change",
    subtitle: "Schedule change approvals",
    pendingCount: 0,
    accent: "#7A5AF8",
    accentSoft: "#F4F1FF",
    icon: <SwapHorizOutlined />,
  },
  {
    title: "Permission",
    key: "Permission",
    view: "permission",
    subtitle: "Permission approvals",
    pendingCount: 10,
    accent: "#00A86B",
    accentSoft: "#EAFBF4",
    icon: <CropRotate />,
  },
  {
    title: "On Duty",
    key: "OnDuty",
    view: "onduty",
    subtitle: "On duty approvals",
    pendingCount: 5,
    accent: "#00A86B",
    accentSoft: "#EAFBF4",
    icon: <CropRotate />,
  },
  {
    title: "Comp Off",
    key: "CompOff",
    view: "compoff",
    subtitle: "Comp off approvals",
    pendingCount: 5,
    accent: "#00A86B",
    accentSoft: "#EAFBF4",
    icon: <CropRotate />,
  },
  {
    title: "Break",
    key: "Break",
    view: "break",
    subtitle: "Break and cancel break approvals",
    pendingCount: 5,
    accent: "#00A86B",
    accentSoft: "#EAFBF4",
    icon: <CropRotate />,
  },
  {
    title: "Advance Salary",
    key: "Advance Salary",
    view: "advance-salary",
    subtitle: "Advance salary approvals",
    pendingCount: 4,
    accent: "#2878FF",
    accentSoft: "#EDF4FF",
    icon: <Savings />,
  },
  {
    title: "Reimbursement",
    key: "Expense",
    view: "expense",
    subtitle: "Reimbursement claim approvals",
    pendingCount: 2,
    accent: "#2F6BFF",
    accentSoft: "#EEF4FF",
    icon: <Savings />,
  },
];

// Colors match the same concepts on Team Workspace Home's summary row
// (WorkforceSummaryCards.js) — pending=orange, approved=green, rejected=red,
// cancelled=neutral slate — so "Pending" reads the same wherever it shows up
// in Team Zone, not just within this one screen.
//
// counts is the team_zone/overall_count/approval-counts response, scoped to
// the same startDate/endDate the date-range filter above already drives.
// Restored per user request after the queue-card redesign below dropped it —
// that redesign only tracks "in queue" (pending) totals per category, so
// this row is the only place Approved/Rejected/Cancelled totals still show.
const aggregateApprovalCounts = (counts, liveCounts = {}) => {
  const c = categoryCountMetrics(counts, liveCounts);
  return sumCountSets(
    c.leave,
    c.overtime,
    c.offDayDuty,
    c.permission,
    c.onDuty,
    c.workAdjustment,
    c.attendanceCorrection,
    c.shiftCorrection,
    c.compOff,
    c.breakRequest,
    c.salaryAdvance,
    c.expenseClaim,
  );
};

const buildSummaryCards = (counts, liveCounts) => {
  const totals = {
    pending: counts?.overall_pending_counts ?? 0,
    approved: counts?.overall_approves_count ?? 0,
    rejected: counts?.overall_rejected_count ?? 0,
    cancelled: counts?.overall_cancelled_count ?? 0,
  };
  return [
    {
      title: "Pending",
      metric: "pending",
      value: totals.pending,
      accent: "#F97316",
      tint: "#FFF7ED",
      detail: "Requests",
      icon: AccessTime,
    },
    {
      title: "Approved",
      metric: "approved",
      value: totals.approved,
      accent: "#16A34A",
      tint: "#EAFBF1",
      detail: "Requests",
      icon: CheckCircleOutline,
    },
    {
      title: "Rejected",
      metric: "rejected",
      value: totals.rejected,
      accent: "#EF4444",
      tint: "#FEF2F2",
      detail: "Requests",
      icon: HighlightOff,
    },
    {
      title: "Cancelled",
      metric: "cancelled",
      value: totals.cancelled,
      accent: designColors.slate600,
      tint: designColors.slate100,
      detail: "Requests",
      icon: RemoveCircleOutline,
    },
  ];
};

// The API has no separate "attendance correction" (WorkAdjustment) category
// — its categories only cover leave_request, overtime_request, off_day_duty,
// permission, on_duty, comp_off, break, salary_advance, expense_claim.
// overall_pending_counts already reconciles to the sum of those, so
// WorkAdjustment's own pending share isn't tracked by this endpoint yet and
// reads as 0 here until it is. Attendance Correction and Shift Change each
// now have their own approve/reject endpoint (see
// attendanceCorrectionApproveHelpers / shiftCorrectionApproveHelpers), but
// this overall-count endpoint doesn't report either as its own category, so
// both badges read 0 here until that's added.
const toCountSet = (entry) => ({
  pending: entry?.pending_counts ?? 0,
  approved: entry?.approves_count ?? 0,
  rejected: entry?.rejected_count ?? 0,
  cancelled: entry?.cancelled_count ?? 0,
});

const categoryCountMetrics = (counts, liveCounts = {}) => {
  const categories = counts?.categories ?? {};
  return {
    leave: liveCounts.leave || toCountSet(categories.leave_request),
    overtime: toCountSet(categories.overtime_request?.overall),
    offDayDuty: toCountSet(categories.off_day_duty?.overall),
    permission: toCountSet(categories.permission),
    onDuty: toCountSet(categories.on_duty),
    workAdjustment: toCountSet(),
    attendanceCorrection: liveCounts.attendanceCorrection || toCountSet(),
    shiftCorrection: liveCounts.shiftCorrection || toCountSet(),
    compOff: toCountSet(categories.comp_off),
    breakRequest: toCountSet(categories.break),
    salaryAdvance: toCountSet(categories.salary_advance),
    expenseClaim: toCountSet(categories.expense_claim),
  };
};

const sumCountSets = (...items) =>
  items.reduce(
    (totals, item) => ({
      pending: totals.pending + (item?.pending ?? 0),
      approved: totals.approved + (item?.approved ?? 0),
      rejected: totals.rejected + (item?.rejected ?? 0),
      cancelled: totals.cancelled + (item?.cancelled ?? 0),
    }),
    { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
  );

// itemKey (used by each overview-card row below) -> its backend
// sub_module_name under Approve's module_details. A few itemKeys don't
// textually match the backend name (CompOff -> "Comp Off", Permission ->
// "Permissions", OnDuty -> "On Duty"), so this can't be a direct string
// comparison — same mapping problem as Sidebar/index.js's
// APPROVE_CATEGORY_SUBMODULE (kept separate here since that map is keyed by
// the flyout's own display name, not this file's itemKey).
const ITEM_KEY_SUBMODULE = {
  Leaves: "Leaves",
  CompOff: "Comp Off",
  "Attendance Correction": "Attendance Correction",
  "Schedule Change": "Schedule Change",
  "Shift Change": "Shift Change",
  Permission: "Permissions",
  OnDuty: "On Duty",
  Break: "Break",
  Overtime: "Overtime",
  "Off Day Duty": "Off Day Duty",
  "Advance Salary": "Advance Salary",
  Expense: "Expense",
};

const buildApprovalOverviewCards = (
  counts,
  approveSubmodules,
  liveCounts,
  metric = "pending",
) => {
  const c = categoryCountMetrics(counts, liveCounts);
  const leaveAndTimeOff = sumCountSets(c.leave, c.compOff);
  const attendanceAndWorkday = sumCountSets(
    c.permission,
    c.onDuty,
    c.workAdjustment,
    c.attendanceCorrection,
    c.shiftCorrection,
    c.breakRequest,
  );
  const additionalWork = sumCountSets(c.overtime, c.offDayDuty);
  const finance = sumCountSets(c.salaryAdvance, c.expenseClaim);

  // Row labels are sentence case ("Leave requests", "Off day duty") to match
  // the redesigned queue-card mock — previously Title Case ("Leave Requests",
  // "Off-Day Duty") to read as standalone chip labels, which this simpler
  // list-row layout no longer needs.
  const cards = [
    {
      title: "Leave & Time Off",
      primaryKey: "Leaves",
      statusCount: toOverviewMetric(leaveAndTimeOff, metric),
      accent: "#6D5DF6",
      accentSoft: "#F4F1FF",
      icon: <AssignmentOutlined sx={{ fontSize: 22 }} />,
      rows: [
        { label: "Leave requests", value: toOverviewMetric(c.leave, metric), itemKey: "Leaves" },
        { label: "Comp off", value: toOverviewMetric(c.compOff, metric), itemKey: "CompOff" },
      ],
      footer: { actionKey: "Leaves" },
    },
    {
      title: "Attendance & Workday",
      primaryKey: "Attendance Correction",
      statusCount: toOverviewMetric(attendanceAndWorkday, metric),
      accent: "#0F9960",
      accentSoft: "#EAFBF4",
      icon: <AccessTimeOutlined sx={{ fontSize: 22 }} />,
      rows: [
        {
          label: "Attendance correction",
          value: toOverviewMetric(c.attendanceCorrection, metric),
          itemKey: "Attendance Correction",
        },
        {
          label: "Schedule change",
          value: toOverviewMetric(c.shiftCorrection, metric),
          itemKey: "Schedule Change",
        },
        {
          label: "Permission",
          value: toOverviewMetric(c.permission, metric),
          itemKey: "Permission",
        },
        { label: "On duty", value: toOverviewMetric(c.onDuty, metric), itemKey: "OnDuty" },
        { label: "Break", value: toOverviewMetric(c.breakRequest, metric), itemKey: "Break" },
      ],
      footer: { actionKey: "Attendance Correction" },
    },
    {
      title: "Additional Work",
      primaryKey: "Overtime",
      statusCount: toOverviewMetric(additionalWork, metric),
      accent: "#F08A24",
      accentSoft: "#FFF4E8",
      icon: <BoltOutlined sx={{ fontSize: 22 }} />,
      rows: [
        { label: "Overtime", value: toOverviewMetric(c.overtime, metric), itemKey: "Overtime" },
        {
          label: "Off day duty",
          value: toOverviewMetric(c.offDayDuty, metric),
          itemKey: "Off Day Duty",
        },
      ],
      footer: { actionKey: "Overtime" },
    },
    {
      title: "Finance",
      primaryKey: "Advance Salary",
      statusCount: toOverviewMetric(finance, metric),
      accent: "#2878FF",
      accentSoft: "#EDF4FF",
      icon: <PaymentsOutlined sx={{ fontSize: 22 }} />,
      rows: [
        {
          label: "Salary advance",
          value: toOverviewMetric(c.salaryAdvance, metric),
          itemKey: "Advance Salary",
        },
        {
          label: "Reimbursement claims",
          value: toOverviewMetric(c.expenseClaim, metric),
          itemKey: "Expense",
        },
      ],
      footer: { actionKey: "Advance Salary" },
    },
  ];

  // Hide any row whose submodule is absent, and drop a whole card once every
  // one of its rows is hidden — same rule the sidebar's flyout list uses. A
  // row without access (viewer/has_access false) stays listed; opening it
  // shows the "right workspace" screen. Until access data has loaded,
  // approveSubmodules is undefined and everything stays visible rather than
  // flashing empty.
  if (!Array.isArray(approveSubmodules)) return cards;

  const isRowAccessible = (row) => {
    const subModuleName = ITEM_KEY_SUBMODULE[row.itemKey];
    if (!subModuleName) return true;
    return approveSubmodules.some((sub) =>
      subModuleNameMatches(sub?.sub_module_name, subModuleName),
    );
  };

  return cards
    .map((card) => ({ ...card, rows: card.rows.filter(isRowAccessible) }))
    .filter((card) => card.rows.length > 0)
    .map((card) => ({
      ...card,
      statusCount: card.rows.reduce((sum, row) => sum + (row.value ?? 0), 0),
    }));
};

const selectedItemFromView = (view) => {
  switch (view) {
    case "overtime":
    case "ot":
      return "Overtime";
    case "offday-duty":
    case "offday":
      return "Off Day Duty";
    case "work-adjustment":
    case "workadjustment":
      return "Attendance Correction";
    case "attendance-correction":
    case "attendancecorrection":
      return "Attendance Correction";
    case "shift-change":
    case "shiftchange":
    case "shift-correction":
    case "shiftcorrection":
      return "Schedule Change";
    case "permission":
      return "Permission";
    case "onduty":
      return "OnDuty";
    case "compoff":
    case "comp-off":
      return "CompOff";
    case "break":
      return "Break";
    case "advance-salary":
    case "advancesalary":
    case "advance":
      return "Advance Salary";
    case "expense":
      return "Expense";
    case "leave":
    case "leaves":
      return "Leaves";
    default:
      return null;
  }
};

// Approval Center always lands on today's date by default — whether that's
// a fresh sidebar navigation, a remount after visiting another Team Zone
// screen, or a brand new tab. The only thing that overrides today is an
// explicit ?date= (Team Workspace Home's navigateToModule) carried on the
// incoming URL; a manual re-pick on this screen's own date-range filter
// still applies for as long as the user stays on this page (see
// handleNavigateToSection below, which deliberately does not reset it when
// switching between approval categories in the same visit), it just isn't
// remembered as the new default the next time Approval Center is opened.
// Default range when no ?date= / ?start_date= is given: This Month, matching
// the "This Month" preset in ApprovalDateRangeFilter (1st -> month end).
const defaultMonthRange = () => {
  const range = getThisMonthRange();
  return { start: range.startDate, end: range.endDate, label: range.label };
};

// Computed once synchronously here (module scope, no hooks) so the very
// first paint already has the right range instead of a one-frame flash of
// "Today" that the location.search effect below would otherwise correct.
// Range used when the URL carries no date. Attendance Correction defaults to
// This Month (1st to month end, same as the date filter's "This Month"
// preset); every other screen defaults to Today.
const defaultDateRangeFor = (item) => {
  if (item === "Attendance Correction") {
    return {
      start: moment().startOf("month").format("YYYY-MM-DD"),
      end: moment().endOf("month").format("YYYY-MM-DD"),
      label: "This Month",
    };
  }
  const today = moment().format("YYYY-MM-DD");
  return { start: today, end: today, label: "Today" };
};

const resolveInitialDateRange = () => {
  const params = new URLSearchParams(window.location.search);
  const urlDate = moment(params.get("date") || "", "YYYY-MM-DD", true);
  if (urlDate.isValid()) {
    const iso = urlDate.format("YYYY-MM-DD");
    return {
      start: iso,
      end: iso,
      label: urlDate.isSame(moment(), "day")
        ? "Today"
        : urlDate.format("DD MMM YYYY"),
    };
  }

  const view = (params.get("view") || params.get("section") || "").toLowerCase();
  return defaultDateRangeFor(selectedItemFromView(view));
};

export const Approve = () => {
  // Lazy initializer, not useState(null) + a mount-time effect to correct
  // it: reading ?view=/?section= synchronously here means the very first
  // paint already has the right category selected. useState(null) used to
  // render one frame as the Approval Center landing page (rail open) before
  // the deep-link effect below flipped selectedItem, which snapped the rail
  // shut via its width transition right after mount — visible as the rail
  // "closing" itself, and whatever UI keys off selectedItem (active
  // highlighting, labels) being briefly wrong. Only happened on a fresh
  // navigation into this page (this initializer only runs once, on mount);
  // switching categories while already here goes directly between two
  // non-null values and never passed through that flash either way.
  const [selectedItem, setSelectedItem] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const view = (
      params.get("view") ||
      params.get("section") ||
      ""
    ).toLowerCase();
    return selectedItemFromView(view);
  });
  const [reCallApi, setReCallApi] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { domain, userScreenAccess, userDetails, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);

  const [startDate, setStartDate] = useState(
    () => resolveInitialDateRange().start,
  );
  const [endDate, setEndDate] = useState(() => resolveInitialDateRange().end);
  const [dateRangeLabel, setDateRangeLabel] = useState(
    () => resolveInitialDateRange().label,
  );
  const [approvalCenterMetric, setApprovalCenterMetric] = useState("pending");
  const [liveApprovalCounts, setLiveApprovalCounts] = useState({});

  // Powers the landing page's summary row and the 4
  // category overview cards — url changing with startDate/endDate makes
  // useFetch refetch on its own when the date-range filter above changes.
  const {
    result: approvalCounts,
    setLoad: reloadApprovalCounts,
    loading: approvalCountsLoading,
  } = useFetch(
    `${domain}team_zone/overall_count/approval-counts/?user_id=${userDetails?.id}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
  );

  useEffect(() => {
    let cancelled = false;
    const fetchLiveApprovalCounts = async () => {
      if (!domain || !userDetails?.id || !topbarCompanyId || !startDate || !endDate) {
        setLiveApprovalCounts({});
        return;
      }

      // customGetAPI swallows its own network/HTTP errors (resolves with
      // {ok:false} or null rather than rejecting), so the one thing that
      // could actually throw here is counting an unexpected response shape.
      // That used to happen inside one shared try/catch around all three
      // categories together, so a single malformed response zeroed out ALL
      // three counts (`setLiveApprovalCounts({})`) -- including the other
      // two categories' perfectly good responses. Isolate each category's
      // own fetch-and-count so one bad response can't blank its siblings,
      // and keep the previous good value for that one category instead of
      // silently dropping it to 0.
      const loadCategoryCount = async (url, categoryLabel) => {
        try {
          const response = await customGetAPI(url);
          return countRowsByStatus(rowsFromResponse(response));
        } catch (error) {
          console.error(
            `[ApprovalCenter] failed to load live queue count for ${categoryLabel}:`,
            error,
          );
          return null;
        }
      };

      const [leaveCount, attendanceCorrectionCount, shiftCorrectionCount] =
        await Promise.all([
          loadCategoryCount(
            `${domain}leave-request/?user_id=${userDetails.id}&department_id=${DEFAULT_ALL_SELECTION}&selected_user_id=${DEFAULT_ALL_SELECTION}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
            "leave",
          ),
          loadCategoryCount(
            `${domain}team_zone/approve/attendance_correction/teamzone-attendance-correction/?user_id=${userDetails.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
            "attendanceCorrection",
          ),
          loadCategoryCount(
            `${domain}team_zone/approve/shift_change/teamzone-shift-change/?user_id=${userDetails.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
            "shiftCorrection",
          ),
        ]);

      if (cancelled) return;

      setLiveApprovalCounts((prev) => ({
        leave: leaveCount ?? prev.leave,
        attendanceCorrection: attendanceCorrectionCount ?? prev.attendanceCorrection,
        shiftCorrection: shiftCorrectionCount ?? prev.shiftCorrection,
      }));
    };

    fetchLiveApprovalCounts();
    return () => {
      cancelled = true;
    };
  }, [domain, endDate, startDate, topbarCompanyId, userDetails?.id]);

  // Every sub-screen (Leave, Overtime, ...) flips reCallApi to true after a
  // successful approve/reject/cancel so its own list refetches — piggyback
  // on that same signal here so the sidebar badges and summary cards catch
  // up immediately instead of only refreshing when the user navigates back
  // to the Approval Center landing page.
  useEffect(() => {
    if (reCallApi) {
      reloadApprovalCounts(true);
    }
  }, [reCallApi, reloadApprovalCounts]);

  useEffect(() => {
    if (!topbarCompanyId) return;
    setReCallApi(true);
    reloadApprovalCounts(true);
  }, [topbarCompanyId, reloadApprovalCounts]);

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Approvals",
  );

  // Permission, OnDuty, and Shift Change are UI-only splits of the
  // WorkAdjustment submodule, so they resolve filter-preference persistence
  // through the WorkAdjustment submodule id. Attendance Correction is NOT in
  // this list: it has its own "Attendance Correction" submodule under
  // Approvals (the name its access check already uses), and looking it up as
  // "WorkAdjustment" left subModuleId undefined -- so saved views and filter
  // preferences silently did nothing on that screen.
  const submoduleLookupName =
    selectedItem === "Permission" ||
    selectedItem === "OnDuty" ||
    selectedItem === "CompOff" ||
    selectedItem === "Break" ||
    selectedItem === "Schedule Change" ||
    selectedItem === "Shift Change"
      ? "WorkAdjustment"
      : selectedItem;

  // Alias-aware match (same SUB_MODULE_NAME_ALIASES table the access checks
  // above already go through via checkEditorPermission/checkViewerPermission)
  // -- a literal `sub_module_name === submoduleLookupName` here silently
  // resolves to undefined for any tenant whose payload uses a known
  // typo/variant (e.g. "Leave Requests" instead of "Leaves"), which the
  // access check would still correctly grant.
  const subModuleId = shiftModule?.submodules?.find((sub) =>
    subModuleNameMatches(sub?.sub_module_name, submoduleLookupName),
  )?.sub_module_id;

  // Temporary diagnostic: if the Approvals module is present but this
  // category's submodule still didn't resolve (even after the alias-aware
  // match above), log the real submodule names the payload actually
  // returned. Advance Salary was reported still broken after that fix even
  // though it isn't part of the old "WorkAdjustment" grouping bug and
  // already matches/aliases correctly against every name seen elsewhere in
  // this codebase ("Advance Salary"/"Salary Advance") -- the remaining
  // possibility is a live payload spelling neither of those covers, or the
  // submodule simply isn't provisioned for this company/role. This makes
  // that visible in the console instead of guessing further. Safe to
  // remove once the real name is confirmed and, if needed, added to
  // SUB_MODULE_NAME_ALIASES in HelperFunction.js.
  useEffect(() => {
    if (shiftModule && selectedItem && !subModuleId) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Approve] No subModuleId resolved for "${selectedItem}" (looked up as "${submoduleLookupName}"). Real sub_module_name values under Approvals:`,
        shiftModule.submodules?.map((sub) => sub?.sub_module_name),
      );
    }
  }, [shiftModule, selectedItem, submoduleLookupName, subModuleId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = (
      params.get("view") ||
      params.get("section") ||
      ""
    ).toLowerCase();
    const nextSelectedItem = selectedItemFromView(view);

    // Team Workspace Home passes the date it currently has selected (see
    // TeamZone/Home/index.js's navigateToModule) so landing here from Home
    // — whether on the plain Approval Center overview or a deep-linked
    // ?view= section — shows that same single day. An explicit ?date=
    // applies that day; anything else (switching between approval
    // categories via the landing page's own cards or the sidebar flyout,
    // or landing on a category with no date param at all) resets back to
    // today rather than silently carrying over whatever range was active on
    // the screen the user just left — e.g. picking a date on Leave and then
    // switching to Comp Off should show Comp Off's own requests for today,
    // not Leave's picked date.
    const urlDate = moment(params.get("date") || "", "YYYY-MM-DD", true);
    const urlStartDate = moment(
      params.get("start_date") || "",
      "YYYY-MM-DD",
      true,
    );
    const urlEndDate = moment(
      params.get("end_date") || "",
      "YYYY-MM-DD",
      true,
    );
    if (urlDate.isValid()) {
      const iso = urlDate.format("YYYY-MM-DD");
      setStartDate(iso);
      setEndDate(iso);
      setDateRangeLabel(resolvePresetLabel(iso, iso));
    } else if (urlStartDate.isValid() && urlEndDate.isValid()) {
      const start = urlStartDate.format("YYYY-MM-DD");
      const end = urlEndDate.format("YYYY-MM-DD");
      setStartDate(start);
      setEndDate(end);
      setDateRangeLabel(resolvePresetLabel(start, end));
    } else {
      const range = defaultDateRangeFor(nextSelectedItem);
      setStartDate(range.start);
      setEndDate(range.end);
      setDateRangeLabel(range.label);
    }

    setReCallApi(true);

    if (!nextSelectedItem) {
      setSelectedItem(null);
      return;
    }

    setSelectedItem(nextSelectedItem);
  }, [location.search, location.key]);

  const handleNavigateToSection = (itemKey) => {
    // Doesn't need to reset startDate/endDate/dateRangeLabel itself — this
    // pushes a plain ?view= with no date param, and the location.search
    // effect below resets the range to today for exactly that case (unless
    // Team Workspace Home's ?date= is present instead). Keeps this in sync
    // with the sidebar flyout, which lands on the same effect.
    if (
      itemKey === "Advance Salary" ||
      itemKey === "Overtime" ||
      itemKey === "Leaves" ||
      itemKey === "Permission" ||
      itemKey === "OnDuty" ||
      itemKey === "CompOff" ||
      itemKey === "Break" ||
      itemKey === "Expense"
    ) {
      setReCallApi(true);
    }

    setSelectedItem(itemKey);
    dispatch(setFilterApi(true));

    const selectedConfig = approvalCenterCards.find(
      (item) => item.key === itemKey,
    );
    const params = new URLSearchParams();

    if (selectedConfig?.view) {
      params.set("view", selectedConfig.view);
    }
    // Carry the current range over, except the untouched "Today" default when
    // opening a screen with its own default (Attendance Correction -> This
    // Month): leaving the date off the URL lets that default apply.
    const today = moment().format("YYYY-MM-DD");
    const isUntouchedToday =
      dateRangeLabel === "Today" && startDate === today && endDate === today;
    const hasOwnDefault =
      defaultDateRangeFor(itemKey).label !== "Today";
    if (startDate && endDate && !(isUntouchedToday && hasOwnDefault)) {
      if (startDate === endDate) {
        params.set("date", startDate);
      } else {
        params.set("start_date", startDate);
        params.set("end_date", endDate);
      }
    }

    history.push({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  const handleBackToApprovalCenter = () => {
    setSelectedItem(null);
    // Refetch the landing page's counts on the way back — an approve/reject
    // action taken in the sub-screen just left would otherwise leave the
    // summary row and overview cards showing pre-action
    // numbers until the next full page load.
    reloadApprovalCounts(true);
    history.push({
      pathname: location.pathname,
      search: "",
    });
  };

  const handleStartDate = async (nextStartDate, nextEndDate) => {
    const normalizedStartDate = normalizeRangeDate(nextStartDate);
    const normalizedEndDate = normalizeRangeDate(nextEndDate);

    if (!normalizedStartDate || !normalizedEndDate) {
      return;
    }

    setStartDate(normalizedStartDate);
    setEndDate(normalizedEndDate);
    setReCallApi(true);
  };

  // Feeds the same startDate/endDate/handleStartDate that the individual
  // approval-type screens (Leave, OverTime, ...) already consume, so a range
  // picked here on the landing page carries over once the user drills into
  // one of them.
  const handleApprovalCenterDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  // The opened approval type's own has_access: a type (e.g. Leave Requests)
  // can be closed while the Approvals module itself is open. ProductAccessGate
  // only sees the module, so the type is checked here. A type missing from
  // the company's payload is blocked too: the sidebar hides it, and after a
  // company switch the open type may not exist in the new company at all.
  const selectedApprovalSubmoduleName = ITEM_KEY_SUBMODULE[selectedItem];
  const selectedApprovalSubmodule = selectedApprovalSubmoduleName
    ? userScreenAccess?.module_details
        ?.find((module) => module?.module_name === "Approvals")
        ?.submodules?.find((sub) =>
          subModuleNameMatches(sub?.sub_module_name, selectedApprovalSubmoduleName),
        )
    : null;
  const selectedApprovalBlocked =
    Boolean(selectedApprovalSubmoduleName) &&
    (!selectedApprovalSubmodule || !nodeHasAccess(selectedApprovalSubmodule));

  const renderMainContent = () => {
    switch (selectedItem) {
      case "Leaves":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Leave
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Overtime":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <OverTime
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Expense":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Expense
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Off Day Duty":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <OffDayDuty
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Attendance Correction":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <AttendanceCorrection
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Schedule Change":
      case "Shift Change":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <ShiftCorrection
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Permission":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Permission
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "OnDuty":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <OnDuty
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "CompOff":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <CompOff
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Break":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Break
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Advance Salary":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <TeamAdvanceSalary
              setReCallApi={setReCallApi}
              reCallApi={reCallApi}
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              handleStartDate={handleStartDate}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      default:
        return null;
    }
  };

  const renderApprovalCenterSkeleton = () => (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Mui.Skeleton
            variant="rounded"
            width={46}
            height={46}
            sx={{ borderRadius: 2 }}
          />
          <Box>
            <Mui.Skeleton variant="text" width={190} height={32} />
            <Mui.Skeleton variant="text" width={320} height={22} />
          </Box>
        </Stack>
        <Mui.Skeleton
          variant="rounded"
          width={220}
          height={40}
          sx={{ borderRadius: 2 }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 1.5,
        }}
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{ p: 1.75, borderRadius: designRadii.card }}
          >
            <Mui.Skeleton variant="text" width="42%" height={20} />
            <Mui.Skeleton variant="text" width="58%" height={34} />
            <Mui.Skeleton variant="text" width="34%" height={18} />
          </Paper>
        ))}
      </Box>

      <Mui.Grid container spacing={1.5}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Mui.Grid item xs={12} md={6} key={index}>
            <Paper
              variant="outlined"
              sx={{
                minHeight: 250,
                p: 2,
                borderRadius: designRadii.card,
              }}
            >
              <Mui.Skeleton variant="text" width="48%" height={28} />
              <Mui.Skeleton variant="text" width="30%" height={22} />
              <Mui.Skeleton variant="rounded" height={1} sx={{ my: 1.5 }} />
              {Array.from({ length: 3 }).map((__, rowIndex) => (
                <Stack
                  key={rowIndex}
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 1.15 }}
                >
                  <Mui.Skeleton variant="text" width="46%" height={22} />
                  <Mui.Skeleton variant="text" width="12%" height={22} />
                </Stack>
              ))}
            </Paper>
          </Mui.Grid>
        ))}
      </Mui.Grid>
    </Stack>
  );

  const renderApprovalCenter = () =>
    approvalCountsLoading ? (
      renderApprovalCenterSkeleton()
    ) : (
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${designColors.tealTint} 0%, #F4FBF8 100%)`,
                color: designColors.tealDark,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ApprovalRounded sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={designType.pageTitle}>Approval Center</Typography>
              <Typography sx={designType.pageSubtitle}>
                A quick overview of all requests waiting for your approval.
              </Typography>
            </Box>
          </Stack>
          <ApprovalDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            dateRangeLabel={dateRangeLabel}
            onChange={handleApprovalCenterDateRange}
            includeCurrentDate
            showToday
            allowFutureDate
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            // Same auto-fit/minmax technique as Home's WorkforceSummaryCards —
            // sizes columns off this row's actual rendered width instead of a
            // window-width breakpoint, so it wraps cleanly under the sidebar
            // regardless of screen size instead of squeezing all 4 into a row
            // that doesn't have room for them.
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            "@media (max-width:599.95px)": {
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {buildSummaryCards(approvalCounts, liveApprovalCounts).map((card) => (
            <StatCard
            activeBorder
              key={card.title}
              {...card}
              isActive={approvalCenterMetric === card.metric}
              onClick={() => setApprovalCenterMetric(card.metric)}
            />
          ))}
        </Box>

        {(() => {
          const approveSubmodules = userScreenAccess?.module_details?.find(
            (module) => module?.module_name === "Approvals",
          )?.submodules;
          const overviewCards = buildApprovalOverviewCards(
            approvalCounts,
            approveSubmodules,
            liveApprovalCounts,
            approvalCenterMetric,
          );
          const metricLabel =
            approvalMetricLabels[approvalCenterMetric] || "pending";
          return (
            <>
              <Box>
                <Typography
                  sx={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: designColors.slate900,
                  }}
                >
                  Approval queues
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: designColors.slate600, mt: 0.25 }}
                >
                  Choose a queue to see the requests inside it.
                </Typography>
              </Box>

              {/* Box wrapper, not a direct Stack child: Grid self-cancels its
                own row-gutter with margin-top:-{gutter} on the container,
                but Stack's spacing rule out-specifies Grid's own generated
                class and silently overrides that margin — so the
                cancellation never fires and this row picks up a stray extra
                ~12px of top padding no sibling row has, throwing off the
                vertical rhythm between rows. Same fix as Team Zone Home's
                index.js. */}
              <Box>
                <Mui.Grid container spacing={2}>
                  {overviewCards.map((card) => {
                    return (
                      <Mui.Grid
                        item
                        xs={12}
                        md={6}
                        key={card.title}
                        sx={{ display: "flex" }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: designRadii.card,
                            border: `1px solid ${designColors.slate200}`,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{ height: 4, backgroundColor: card.accent }}
                          />
                          <Box
                            sx={{
                              p: 2.5,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.75,
                              flexGrow: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Box
                                  sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: card.accent,
                                    backgroundColor: card.accentSoft,
                                    flexShrink: 0,
                                  }}
                                >
                                  {card.icon}
                                </Box>
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: 15,
                                      fontWeight: 700,
                                      color: designColors.slate900,
                                    }}
                                  >
                                    {card.title}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12.5,
                                      color: designColors.slate500,
                                      mt: 0.15,
                                    }}
                                  >
                                    {card.statusCount} request
                                    {card.statusCount === 1 ? "" : "s"}{" "}
                                    {metricLabel}
                                  </Typography>
                                </Box>
                              </Stack>
                              <ButtonBase
                                onClick={() =>
                                  handleNavigateToSection(card.primaryKey)
                                }
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "50%",
                                  backgroundColor: designColors.slate100,
                                  color: designColors.slate600,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <KeyboardArrowRight sx={{ fontSize: 20 }} />
                              </ButtonBase>
                            </Stack>

                            <Stack
                              divider={
                                <Mui.Divider
                                  sx={{ borderColor: designColors.slate100 }}
                                />
                              }
                              sx={{ flexGrow: 1 }}
                            >
                              {card.rows.map((row) => (
                                <ButtonBase
                                  key={row.label}
                                  onClick={() =>
                                    handleNavigateToSection(row.itemKey)
                                  }
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.1,
                                    textAlign: "left",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 13.5,
                                      fontWeight: 500,
                                      color: designColors.slate700,
                                    }}
                                  >
                                    {row.label}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        color: designColors.slate900,
                                      }}
                                    >
                                      {row.value}
                                    </Typography>
                                    <KeyboardArrowRight
                                      sx={{
                                        fontSize: 18,
                                        color: designColors.slate400,
                                      }}
                                    />
                                  </Stack>
                                </ButtonBase>
                              ))}
                            </Stack>

                            <Stack
                              direction="row"
                              justifyContent="flex-end"
                              alignItems="center"
                              sx={{
                                pt: 1.25,
                                mt: "auto",
                                borderTop: `1px solid ${designColors.slate100}`,
                              }}
                            >
                              <ButtonBase
                                onClick={() =>
                                  handleNavigateToSection(
                                    card.footer.actionKey || card.primaryKey,
                                  )
                                }
                                sx={{
                                  color: card.accent,
                                  fontWeight: 700,
                                  fontSize: 12.5,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                View queue{" "}
                                <ArrowForward sx={{ fontSize: 14 }} />
                              </ButtonBase>
                            </Stack>
                          </Box>
                        </Paper>
                      </Mui.Grid>
                    );
                  })}
                </Mui.Grid>
              </Box>
            </>
          );
        })()}
      </Stack>
    );

  return (
    <ThemeProvider theme={designTheme}>
      <Mui.Box
        sx={{
          backgroundColor: designColors.pageBg,
          // Sidebar renders an 80px header spacer and a 1px divider in flow.
          // Account for both so empty approval lists fit the viewport.
          minHeight: `calc(100vh - ${APP_BAR_HEIGHT + 1}px)`,
          display: "flex",
        }}
      >
        <Mui.Box
          sx={{
            flexGrow: 1,
            px: { xs: 1.5, md: 2.5 },
            pt: 2.5,
            pb: 2.5,
            minWidth: 0,
          }}
        >
          {!(approvalCountsLoading && !selectedItem) && (
            <GlobalBreadcrumb
              sx={{ mb: 2 }}
              trail={[
                {
                  label: "Team Workspace",
                  onClick: () => history.push("/admin/teamzone/home"),
                },
                ...(selectedItem
                  ? [
                      {
                        label: "Approval Center",
                        onClick: handleBackToApprovalCenter,
                      },
                      selectedItem,
                    ]
                  : ["Approval Center"]),
              ]}
            />
          )}

          {selectedItem
            ? selectedApprovalBlocked
              ? <WorkspaceUnavailableScreen />
              : renderMainContent()
            : renderApprovalCenter()}
        </Mui.Box>
      </Mui.Box>
    </ThemeProvider>
  );
};
